import React, {Suspense, useState} from 'react';
import {ReferralData} from '../../../model/user/referrals/Referrals';
import {ReferralService} from '../../../service/ReferralService';
import {useToast} from '../../ui/toast/ToastContext';
import AnimatedButton from '../../ui/motion/AnimatedButton';
import LoadingComponent from '../../common/LoadingComponent';
import {Await, Link, useLoaderData} from 'react-router-dom';
import ErrorContent from '../../common/error/ErrorContent';
import {motion} from 'framer-motion';
import {useUser} from '../../../context/UserContext';

const glassCard = {
    background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.35) 0%, rgba(26, 26, 46, 0.55) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
};

const glassCardLight = {
    background: 'linear-gradient(145deg, rgba(42, 51, 66, 0.25) 0%, rgba(26, 26, 46, 0.4) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.04)',
};

function fadeUp(delay: number) {
    return {
        initial: {opacity: 0, y: 25},
        animate: {opacity: 1, y: 0},
        transition: {duration: 0.55, delay, ease: [0.25, 0.1, 0.25, 1]}
    };
}

function ReferralsContent({initialData}: { initialData: ReferralData }) {
    const [referralData, setReferralData] = useState<ReferralData>(initialData);
    const [generatingCode, setGeneratingCode] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const {showMessage} = useToast();

    async function generateCode() {
        try {
            setGeneratingCode(true);
            const responseKED = await ReferralService.generateReferralCode({type: 'KED'});
            await ReferralService.generateReferralCode({type: 'PLATFORM'});
            showMessage(`Kod polecenia został wygenerowany: ${responseKED.code}`);
            const newData = await ReferralService.getReferralData();
            setReferralData(newData);
        } catch (error) {
            console.error('Error generating referral code:', error);
            showMessage('Błąd podczas generowania kodu polecenia', 'error');
        } finally {
            setGeneratingCode(false);
        }
    }

    function copyToClipboard(text: string, type: 'code' | 'link') {
        navigator.clipboard.writeText(text);
        showMessage(type === 'code' ? 'Kod został skopiowany do schowka' : 'Link został skopiowany do schowka');
    }

    function getReferralLink(code: string, type: 'KED' | 'PLATFORM', variant?: 'teen') {
        const baseUrl = window.location.origin;
        const path = type === 'KED'
            ? `/polecenie/kurs-efektywnego-dzialania${variant === 'teen' ? '/uczen' : ''}`
            : '/polecenie/platforma';
        return `${baseUrl}${path}?ref=${code}`;
    }

    function formatDate(dateString: string) {
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {year: 'numeric', month: 'long', day: 'numeric'});
    }

    const hasCode = referralData?.kedCode || referralData?.platformCode;
    const code = referralData?.kedCode?.code || referralData?.platformCode?.code || '';
    const usages = referralData?.usages || [];
    const payouts = referralData?.payouts || [];
    const emailVerified = referralData?.emailVerified ?? false;

    const POLISH_MONTHS = [
        'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
        'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
    ];

    const totalPaid = payouts.reduce((sum, p) => sum + p.amount, 0);

    return (
        <div className="mw-md-5xl mx-auto">

            {/* ── Hero ── */}
            <motion.div className="text-center mb-6" {...fadeUp(0)}>
                <span
                    className="badge text-uppercase px-3 py-2 mb-3"
                    style={{
                        background: 'rgba(42, 209, 103, 0.12)',
                        color: '#2ad167',
                        fontSize: '0.7rem',
                        border: '1px solid rgba(42, 209, 103, 0.25)',
                        letterSpacing: '0.08em'
                    }}
                >
                    Program poleceń
                </span>
                <h1
                    className="font-heading text-white mb-3"
                    style={{fontSize: 'clamp(2rem, 4vw, 2.75rem)', letterSpacing: '-0.03em', lineHeight: 1.15}}
                >
                    Polecaj i&nbsp;zarabiaj
                </h1>
                <p className="text-secondary-light mx-auto mb-0"
                   style={{maxWidth: '600px', fontSize: '1.05rem', lineHeight: 1.7}}>
                    Zarabiaj <strong className="text-success">20%</strong> od każdego zakupu dokonanego przez Twoich
                    znajomych
                </p>
            </motion.div>

            {/* ── Earning potential ── */}
            <motion.div className="row g-4 mb-5" {...fadeUp(0.1)}>
                <div className="col-md-6">
                    <div className="rounded-4 p-4 p-lg-5 h-100 position-relative overflow-hidden" style={glassCard}>
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                            background: 'linear-gradient(90deg, #2ad167 0%, rgba(42, 209, 103, 0.1) 100%)'
                        }}/>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
                                    stroke="#2ad167" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span className="text-success fw-semibold"
                                  style={{fontSize: '0.8rem', letterSpacing: '0.04em', textTransform: 'uppercase'}}>
                                Kurs Efektywnego Działania
                            </span>
                        </div>
                        <div className="mb-3">
                            <span className="text-secondary-light" style={{fontSize: '0.85rem'}}>Cena kursu (tylko z polecenia)</span>
                            <h3 className="text-white font-heading mb-0" style={{letterSpacing: '-0.02em'}}>3 000
                                zł</h3>
                        </div>
                        <div className="rounded-3 p-3" style={{
                            background: 'rgba(42, 209, 103, 0.08)',
                            border: '1px solid rgba(42, 209, 103, 0.15)'
                        }}>
                            <span className="text-secondary-light d-block" style={{fontSize: '0.78rem'}}>Twoja prowizja za polecenie</span>
                            <span className="text-success fw-bold" style={{fontSize: '1.5rem'}}>+600 zł</span>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="rounded-4 p-4 p-lg-5 h-100 position-relative overflow-hidden" style={glassCard}>
                        <div style={{
                            position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                            background: 'linear-gradient(90deg, #f59e0c 0%, rgba(245, 158, 12, 0.1) 100%)'
                        }}/>
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25Z"
                                    stroke="#f59e0c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span className="text-warning fw-semibold"
                                  style={{fontSize: '0.8rem', letterSpacing: '0.04em', textTransform: 'uppercase'}}>
                                Produkacja OS
                            </span>
                        </div>
                        <div className="mb-3">
                            <span className="text-secondary-light" style={{fontSize: '0.85rem'}}>Cena platformy (tylko z polecenia)</span>
                            <h3 className="text-white font-heading mb-0" style={{letterSpacing: '-0.02em'}}>79,20
                                zł<span className="text-secondary-light fw-normal"
                                        style={{fontSize: '0.9rem'}}> /msc</span></h3>
                        </div>
                        <div className="rounded-3 p-3" style={{
                            background: 'rgba(245, 158, 12, 0.08)',
                            border: '1px solid rgba(245, 158, 12, 0.15)'
                        }}>
                            <span className="text-secondary-light d-block" style={{fontSize: '0.78rem'}}>Twoja prowizja za polecenie</span>
                            <span className="text-warning fw-bold" style={{fontSize: '1.5rem'}}>+15,84 zł<span
                                className="fw-normal text-secondary-light"
                                style={{fontSize: '0.85rem'}}> /msc</span></span>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ── How it works ── */}
            <motion.div className="mb-5" {...fadeUp(0.2)}>
                <div className="rounded-4 p-4 p-md-5" style={glassCard}>
                    <h4 className="text-white mb-4 fw-semibold" style={{fontSize: '1.1rem'}}>
                        Jak to działa?
                    </h4>
                    <div className="row g-4">
                        {[
                            {
                                n: '1',
                                icon: 'bi-key',
                                title: 'Wygeneruj kod',
                                desc: 'Kliknij przycisk poniżej, aby otrzymać swój unikalny kod polecenia'
                            },
                            {
                                n: '2',
                                icon: 'bi-share',
                                title: 'Podziel się linkiem',
                                desc: 'Wyślij link znajomym, rodzinie lub udostępnij w social media'
                            },
                            {n: '3', icon: 'bi-cart-check', title: 'Znajomy kupuje', desc: 'Twój znajomy otrzymuje specjalną zniżkę na zakup'},
                            {
                                n: '4',
                                icon: 'bi-cash-coin',
                                title: 'Zarabiasz 20%',
                                desc: 'Prowizja trafia na Twoje konto raz w miesiącu'
                            }
                        ].map(function (step, idx) {
                            return (
                                <div key={step.n} className="col-6 col-md-3">
                                    <div className="text-center position-relative">
                                        {idx < 3 && (
                                            <div className="d-none d-md-block position-absolute" style={{
                                                top: '24px',
                                                left: 'calc(50% + 32px)',
                                                width: 'calc(100% - 16px)',
                                                height: '1px',
                                                background: 'linear-gradient(90deg, rgba(42, 209, 103, 0.25) 0%, rgba(42, 209, 103, 0.05) 100%)',
                                            }}/>
                                        )}
                                        <div
                                            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 position-relative"
                                            style={{
                                                width: '48px', height: '48px',
                                                background: 'rgba(42, 209, 103, 0.1)',
                                                border: '1px solid rgba(42, 209, 103, 0.2)',
                                            }}
                                        >
                                            <span className="text-success fw-bold">{step.n}</span>
                                            <i className={`bi ${step.icon} position-absolute`} style={{
                                                fontSize: '1.8rem',
                                                color: 'rgba(42, 209, 103, 0.06)',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                pointerEvents: 'none',
                                            }}></i>
                                        </div>
                                        <h6 className="text-white mb-1" style={{fontSize: '0.9rem'}}>{step.title}</h6>
                                        <p className="text-secondary-light mb-0"
                                           style={{fontSize: '0.8rem', lineHeight: 1.6}}>
                                            {step.desc}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </motion.div>

            {/* ── Your referral code ── */}
            <motion.div className="mb-5" {...fadeUp(0.3)}>
                <div className="rounded-4 p-4 p-md-5" style={glassCard}>
                    <h4 className="text-white mb-4 fw-semibold" style={{fontSize: '1.1rem'}}>
                        Twój kod polecenia
                    </h4>

                    {hasCode ? (
                        <div>
                            {/* Code showcase */}
                            <div className="text-center mb-4">
                                <div className="d-inline-block rounded-4 px-4 px-md-5 py-4 position-relative" style={{
                                    background: 'linear-gradient(135deg, rgba(42, 209, 103, 0.06) 0%, rgba(42, 209, 103, 0.02) 100%)',
                                    border: '1px solid rgba(42, 209, 103, 0.18)',
                                    boxShadow: '0 0 40px rgba(42, 209, 103, 0.04)',
                                }}>
                                    <div className="text-secondary-light mb-2"
                                         style={{fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase'}}>
                                        Twój unikalny kod
                                    </div>
                                    <div className="text-white fw-bold mb-3" style={{
                                        fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                                        letterSpacing: '0.25em',
                                        fontFamily: '"Courier New", Courier, monospace',
                                    }}>
                                        {code}
                                    </div>
                                    <AnimatedButton
                                        className="btn btn-success px-4 py-2"
                                        onClick={() => copyToClipboard(code, 'code')}
                                        style={{borderRadius: '12px'}}
                                    >
                                        <i className="bi bi-clipboard me-2"></i>Kopiuj kod
                                    </AnimatedButton>
                                </div>
                            </div>

                            {/* Copy link buttons */}
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <div
                                        className="rounded-3 p-3 p-md-4 h-100"
                                        role="button"
                                        onClick={() => copyToClipboard(getReferralLink(code, 'KED'), 'link')}
                                        style={{
                                            ...glassCardLight,
                                            cursor: 'pointer',
                                            transition: 'border-color 0.2s, background 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(42, 209, 103, 0.25)';
                                            e.currentTarget.style.background = 'rgba(42, 209, 103, 0.06)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = glassCardLight.border.replace('1px solid ', '');
                                            e.currentTarget.style.background = glassCardLight.background;
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <div className="text-success fw-semibold mb-1" style={{fontSize: '0.85rem'}}>
                                                    <i className="bi bi-book me-2" style={{fontSize: '0.8rem'}}></i>
                                                    KED — Dla rodzica
                                                </div>
                                                <div className="text-secondary-light" style={{fontSize: '0.75rem'}}>
                                                    Kliknij, aby skopiować link polecenia
                                                </div>
                                            </div>
                                            <i className="bi bi-copy text-success" style={{fontSize: '1.1rem', opacity: 0.7}}></i>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div
                                        className="rounded-3 p-3 p-md-4 h-100"
                                        role="button"
                                        onClick={() => copyToClipboard(getReferralLink(code, 'KED', 'teen'), 'link')}
                                        style={{
                                            ...glassCardLight,
                                            cursor: 'pointer',
                                            transition: 'border-color 0.2s, background 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(42, 209, 103, 0.25)';
                                            e.currentTarget.style.background = 'rgba(42, 209, 103, 0.06)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = glassCardLight.border.replace('1px solid ', '');
                                            e.currentTarget.style.background = glassCardLight.background;
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <div className="text-success fw-semibold mb-1" style={{fontSize: '0.85rem'}}>
                                                    <i className="bi bi-mortarboard me-2" style={{fontSize: '0.8rem'}}></i>
                                                    KED — Dla ucznia
                                                </div>
                                                <div className="text-secondary-light" style={{fontSize: '0.75rem'}}>
                                                    Kliknij, aby skopiować link polecenia
                                                </div>
                                            </div>
                                            <i className="bi bi-copy text-success" style={{fontSize: '1.1rem', opacity: 0.7}}></i>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div
                                        className="rounded-3 p-3 p-md-4 h-100"
                                        role="button"
                                        onClick={() => copyToClipboard(getReferralLink(code, 'PLATFORM'), 'link')}
                                        style={{
                                            ...glassCardLight,
                                            cursor: 'pointer',
                                            transition: 'border-color 0.2s, background 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = 'rgba(245, 158, 12, 0.25)';
                                            e.currentTarget.style.background = 'rgba(245, 158, 12, 0.06)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = glassCardLight.border.replace('1px solid ', '');
                                            e.currentTarget.style.background = glassCardLight.background;
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <div className="text-warning fw-semibold mb-1" style={{fontSize: '0.85rem'}}>
                                                    <i className="bi bi-display me-2" style={{fontSize: '0.8rem'}}></i>
                                                    Produkacja OS
                                                </div>
                                                <div className="text-secondary-light" style={{fontSize: '0.75rem'}}>
                                                    Kliknij, aby skopiować link polecenia
                                                </div>
                                            </div>
                                            <i className="bi bi-copy text-warning" style={{fontSize: '1.1rem', opacity: 0.7}}></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : !emailVerified ? (
                        <div className="text-center py-4">
                            <div className="rounded-4 p-4 mb-4" style={{
                                background: 'rgba(245, 158, 12, 0.06)',
                                border: '1px solid rgba(245, 158, 12, 0.2)'
                            }}>
                                <i className="bi bi-envelope-exclamation text-warning fs-1 mb-3 d-block"></i>
                                <h5 className="text-warning mb-3">Potwierdź swój adres email</h5>
                                <p className="text-secondary-light mb-3">
                                    Aby móc wygenerować kod polecenia i dołączyć do programu poleceń,
                                    musisz najpierw potwierdzić swój adres email.
                                </p>
                                <p className="text-secondary-light small mb-0">
                                    Sprawdź swoją skrzynkę pocztową i kliknij w link weryfikacyjny,
                                    który został wysłany po rejestracji. Jeśli nie widzisz wiadomości,
                                    sprawdź folder spam.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-secondary-light mb-4">
                                Nie masz jeszcze kodu poleceń. Wygeneruj go, aby zacząć zarabiać!
                            </p>

                            <div className="form-check d-inline-flex align-items-center mb-4">
                                <input
                                    className="form-check-input me-2"
                                    type="checkbox"
                                    id="termsAcceptance"
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                />
                                <label className="form-check-label text-secondary-light small"
                                       htmlFor="termsAcceptance">
                                    Akceptuję{' '}
                                    <Link target="_blank" className="text-success" to="/regulamin">
                                        regulamin programu poleceń
                                    </Link>
                                </label>
                            </div>

                            <div>
                                <AnimatedButton
                                    className="btn btn-success btn-lg px-5"
                                    onClick={() => generateCode()}
                                    disabled={generatingCode || !termsAccepted}
                                >
                                    {generatingCode ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Generowanie...
                                        </>
                                    ) : (
                                        'Wygeneruj kod polecenia'
                                    )}
                                </AnimatedButton>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* ── Statistics ── */}
            <motion.div className="mb-5" {...fadeUp(0.4)}>
                <div className="rounded-4 p-4 p-md-5" style={glassCard}>
                    <h4 className="text-white mb-4 fw-semibold" style={{fontSize: '1.1rem'}}>
                        Twoje statystyki
                    </h4>
                    <div className="row g-3">
                        {[
                            {
                                value: referralData?.statistics?.totalReferrals || 0,
                                label: 'Wszystkie polecenia',
                                color: '#2ad167',
                                suffix: ''
                            },
                            {
                                value: referralData?.statistics?.kedReferrals || 0,
                                label: 'Polecenia KED',
                                color: '#2ad167',
                                suffix: ''
                            },
                            {
                                value: referralData?.statistics?.platformReferrals || 0,
                                label: 'Polecenia Platformy',
                                color: '#f59e0c',
                                suffix: ''
                            },
                            {
                                value: referralData?.statistics?.totalEarnings || 0,
                                label: 'Zarobione',
                                color: '#2ad167',
                                suffix: ' zł'
                            }
                        ].map((stat) => (
                            <div key={stat.label} className="col-6 col-md-3">
                                <div className="rounded-3 p-3 p-lg-4 text-center h-100" style={glassCardLight}>
                                    <h2 className="mb-1 font-heading"
                                        style={{color: stat.color, letterSpacing: '-0.02em'}}>
                                        {stat.value}{stat.suffix}
                                    </h2>
                                    <p className="text-secondary-light mb-0"
                                       style={{fontSize: '0.78rem'}}>{stat.label}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {referralData?.statistics?.pendingEarnings && referralData.statistics.pendingEarnings > 0 && (
                        <div className="mt-4 rounded-3 p-3" style={{
                            background: 'rgba(42, 209, 103, 0.06)',
                            border: '1px solid rgba(42, 209, 103, 0.15)'
                        }}>
                            <i className="bi bi-clock-history me-2 text-success"></i>
                            <strong className="text-white">{referralData.statistics.pendingEarnings} zł</strong>
                            <span className="text-secondary-light"> oczekuje na zatwierdzenie (prowizja za KED w okresie gwarancji)</span>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* ── Important info ── */}
            <motion.div className="mb-5" {...fadeUp(0.45)}>
                <div className="rounded-4 p-4 p-md-5" style={glassCard}>
                    <h4 className="text-white mb-4 fw-semibold" style={{fontSize: '1.1rem'}}>
                        Ważne informacje
                    </h4>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="rounded-3 p-4 h-100" style={glassCardLight}>
                                <h6 className="text-white mb-3" style={{fontSize: '0.88rem'}}>
                                    <i className="bi bi-clock-history me-2 text-success"></i>
                                    Naliczanie prowizji
                                </h6>
                                <ul className="mb-0 text-secondary-light ps-3"
                                    style={{fontSize: '0.83rem', lineHeight: 1.8}}>
                                    <li className="mb-1">
                                        <strong className="text-white">KED:</strong> prowizja oczekuje 31 dni (okres
                                        gwarancji zwrotu)
                                    </li>
                                    <li className="mb-1">
                                        <strong className="text-white">Platforma:</strong> prowizja naliczana od razu po
                                        zakupie
                                    </li>
                                    <li>
                                        W przypadku zwrotu, prowizja nie zostanie naliczona
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="rounded-3 p-4 h-100" style={glassCardLight}>
                                <h6 className="text-white mb-3" style={{fontSize: '0.88rem'}}>
                                    <i className="bi bi-cash-stack me-2 text-success"></i>
                                    Wypłaty prowizji
                                </h6>
                                <ul className="mb-0 text-secondary-light ps-3"
                                    style={{fontSize: '0.83rem', lineHeight: 1.8}}>
                                    <li className="mb-1">
                                        <strong className="text-white">Termin:</strong> raz w miesiącu, do 10. dnia
                                        następnego miesiąca
                                    </li>
                                    <li className="mb-1">
                                        <strong className="text-white">Minimalna kwota:</strong> 100 zł
                                    </li>
                                    <li>
                                        Kontakt: <a href="mailto:kontakt@produkacja.pl"
                                                    className="text-success">kontakt@produkacja.pl</a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 rounded-3 p-3"
                         style={{background: 'rgba(245, 158, 12, 0.06)', border: '1px solid rgba(245, 158, 12, 0.12)'}}>
                        <small className="text-secondary-light">
                            <i className="bi bi-info-circle me-2 text-warning"></i>
                            Nie możesz użyć własnego kodu polecenia do swoich zakupów.
                        </small>
                    </div>
                </div>
            </motion.div>

            {/* ── Referral history ── */}
            {usages.length > 0 && (
                <motion.div className="mb-5" {...fadeUp(0.5)}>
                    <div className="rounded-4 p-4 p-md-5" style={glassCard}>
                        <h4 className="text-white mb-4 fw-semibold" style={{fontSize: '1.1rem'}}>
                            Historia poleceń
                        </h4>
                        <div className="table-responsive">
                            <table className="table table-hover mb-0" style={{background: 'transparent'}}>
                                <thead>
                                <tr>
                                    <th className="border-0 ps-0 text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Data
                                    </th>
                                    <th className="border-0 text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Produkt
                                    </th>
                                    <th className="border-0 text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Polecony
                                    </th>
                                    <th className="border-0 text-end text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Kwota
                                    </th>
                                    <th className="border-0 text-end text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Prowizja
                                    </th>
                                    <th className="border-0 text-end pe-0 text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Status
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {usages.map((usage, index) => (
                                    <tr key={index} style={{borderColor: 'rgba(255,255,255,0.04)'}}>
                                        <td className="ps-0 text-white"
                                            style={{fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.04)'}}>
                                            {formatDate(usage.createdAt)}
                                        </td>
                                        <td style={{borderColor: 'rgba(255,255,255,0.04)'}}>
                                            <span
                                                className={`badge ${usage.type === 'KED' ? 'text-success' : 'text-warning'}`}
                                                style={{
                                                    background: usage.type === 'KED' ? 'rgba(42,209,103,0.1)' : 'rgba(245,158,12,0.1)',
                                                    border: `1px solid ${usage.type === 'KED' ? 'rgba(42,209,103,0.2)' : 'rgba(245,158,12,0.2)'}`,
                                                    fontSize: '0.72rem'
                                                }}>
                                                {usage.type === 'KED' ? 'KED' : 'Platforma'}
                                            </span>
                                        </td>
                                        <td className="text-secondary-light"
                                            style={{fontSize: '0.83rem', borderColor: 'rgba(255,255,255,0.04)'}}>
                                            {usage.refereeEmail || '—'}
                                        </td>
                                        <td className="text-end text-white"
                                            style={{fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.04)'}}>
                                            {usage.purchaseAmount.toLocaleString('pl-PL')} zł
                                        </td>
                                        <td className="text-end text-success fw-bold"
                                            style={{fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.04)'}}>
                                            +{usage.commissionEarned.toLocaleString('pl-PL')} zł
                                        </td>
                                        <td className="text-end pe-0" style={{borderColor: 'rgba(255,255,255,0.04)'}}>
                                            {usage.status === 'CONFIRMED' ? (
                                                <span className="badge text-success" style={{
                                                    background: 'rgba(42,209,103,0.1)',
                                                    border: '1px solid rgba(42,209,103,0.2)',
                                                    fontSize: '0.7rem'
                                                }}>
                                                    <i className="bi bi-check-circle me-1"></i>Potwierdzone
                                                </span>
                                            ) : (
                                                <span className="badge text-warning" style={{
                                                    background: 'rgba(245,158,12,0.1)',
                                                    border: '1px solid rgba(245,158,12,0.2)',
                                                    fontSize: '0.7rem'
                                                }}>
                                                    <i className="bi bi-clock me-1"></i>Oczekuje
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* ── Payouts history ── */}
            {payouts.length > 0 && (
                <motion.div {...fadeUp(0.55)}>
                    <div className="rounded-4 p-4 p-md-5" style={glassCard}>
                        <h4 className="text-white mb-4 fw-semibold" style={{fontSize: '1.1rem'}}>
                            Historia wypłat
                        </h4>
                        <div className="table-responsive">
                            <table className="table table-hover mb-0" style={{background: 'transparent'}}>
                                <thead>
                                <tr>
                                    <th className="border-0 ps-0 text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Okres
                                    </th>
                                    <th className="border-0 text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Data wypłaty
                                    </th>
                                    <th className="border-0 text-end pe-0 text-secondary-light fw-medium"
                                        style={{fontSize: '0.8rem'}}>Kwota
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                {payouts.map((payout, index) => (
                                    <tr key={index} style={{borderColor: 'rgba(255,255,255,0.04)'}}>
                                        <td className="ps-0 text-white"
                                            style={{fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.04)'}}>
                                            {POLISH_MONTHS[payout.month - 1]} {payout.year}
                                        </td>
                                        <td className="text-secondary-light"
                                            style={{fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.04)'}}>
                                            {formatDate(payout.paidAt)}
                                        </td>
                                        <td className="text-end pe-0 text-success fw-bold"
                                            style={{fontSize: '0.85rem', borderColor: 'rgba(255,255,255,0.04)'}}>
                                            {payout.amount.toLocaleString('pl-PL')} zł
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                                <tfoot>
                                <tr style={{borderColor: 'rgba(255,255,255,0.08)'}}>
                                    <td className="ps-0 text-white fw-bold" colSpan={2}
                                        style={{borderColor: 'rgba(255,255,255,0.08)'}}>
                                        Razem wypłacono
                                    </td>
                                    <td className="text-end pe-0 text-success fw-bold"
                                        style={{borderColor: 'rgba(255,255,255,0.08)'}}>
                                        {totalPaid.toLocaleString('pl-PL')} zł
                                    </td>
                                </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    );
}

export default function Referrals() {
    const loaderData = useLoaderData() as { referralData: Promise<ReferralData> };
    const {user} = useUser();

    if (user?.paidFullPrice) {
        return (
            <div className="mw-md-5xl mx-auto">
                <motion.div {...fadeUp(0)}>
                    <div className="text-center mb-6">
                        <span
                            className="badge text-uppercase px-3 py-2 mb-3"
                            style={{
                                background: 'rgba(42, 209, 103, 0.12)',
                                color: '#2ad167',
                                fontSize: '0.7rem',
                                border: '1px solid rgba(42, 209, 103, 0.25)',
                                letterSpacing: '0.08em'
                            }}
                        >
                            Polecenia
                        </span>
                        <h1 className="font-heading text-white mb-3"
                            style={{fontSize: 'clamp(2rem, 4vw, 2.75rem)', letterSpacing: '-0.03em'}}>
                            Program poleceń
                        </h1>
                    </div>
                    <div className="rounded-4 p-4 p-md-5 text-center" style={glassCard}>
                        <i className="bi bi-info-circle text-warning fs-1 mb-3 d-block"></i>
                        <p className="text-secondary-light mb-0">
                            Program poleceń jest obecnie niedostępny.
                        </p>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <Suspense fallback={<LoadingComponent/>}>
            <Await
                resolve={loaderData.referralData}
                errorElement={<ErrorContent/>}
            >
                {(referralData) => <ReferralsContent initialData={referralData}/>}
            </Await>
        </Suspense>
    );
}
